import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { AdminTenantGroupService } from '../../../services/admin-tenant-group.service';
import { ToastService } from '../../../services/toast-service';
import { CreateGroupModalComponent } from './create-group-modal/create-group-modal.component';
import { EditGroupModalComponent } from './edit-group-modal/edit-group-modal.component';
import { AppSettings } from '../../../shared/app-settings';

@Component({
  selector: 'app-tenant-group-management',
  templateUrl: './tenant-group-management.component.html',
  styleUrls: ['./tenant-group-management.component.scss']
})
export class TenantGroupManagementComponent implements OnInit {
  tenantGroups: any = { rows: [], totalCount: 0 };
  columns: any[] = [];
  loading = false;
  isUserAdmin = false;
  
  // Delete confirmation
  @ViewChild('deleteConfirmModal') deleteConfirmModal!: TemplateRef<any>;
  groupToDelete: any = null;
  private modalRef?: NgbModalRef;

  constructor(
    private adminService: AdminTenantGroupService,
    private modalService: NgbModal,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadUserAdminState();
    this.setupColumns();
    this.loadTenantGroups();
  }

  loadUserAdminState(): void {
    // Match the storage convention used by news.component / login flow:
    // canonical key is AppSettings.StorageKeys.User (= 'user'); the legacy
    // 'userData' key is kept as a fallback for older sessions.
    const rawUserData = localStorage.getItem(AppSettings.StorageKeys.User)
      || localStorage.getItem('userData');
    if (!rawUserData) {
      this.isUserAdmin = false;
      return;
    }

    try {
      const parsed = JSON.parse(rawUserData);
      this.isUserAdmin = parsed?.userAdmin === '1' || parsed?.userAdmin === 1 || parsed?.userAdmin === true;
    } catch {
      this.isUserAdmin = false;
    }
  }

  setupColumns(): void {
    this.columns = [
      {
        field: 'groupID',
        title: this.translateService.instant('TenantGroupManagement.groupID'),
        width: 100,
        hidden: true
      },
      {
        field: 'groupName',
        title: this.translateService.instant('TenantGroupManagement.groupName'),
        width: 200
      },
      {
        field: 'description',
        title: this.translateService.instant('TenantGroupManagement.description'),
        width: 300
      },
      {
        field: 'memberCount',
        title: this.translateService.instant('TenantGroupManagement.memberCount'),
        width: 120,
        template: (row: any) => row.memberCount || 0
      },
      {
        field: 'createdDate',
        title: this.translateService.instant('General.createdDate'),
        width: 150,
        type: 'date'
      },
      {
        field: 'actions',
        title: this.translateService.instant('General.actions'),
        width: 150,
        filterable: false,
        sortable: false,
        template: 'actions'
      }
    ];
  }

  loadTenantGroups(): void {
    this.loading = true;
    this.adminService.getAllGroups().subscribe({
      next: (response) => {
        if (response?.success) {
          this.tenantGroups = {
            rows: response.result || [],
            totalCount: response.result?.length || 0
          };
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading tenant groups:', error);
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('TenantGroupManagement.loadFailed')]
        });
        this.loading = false;
      }
    });
  }

  openCreateModal(): void {
    if (!this.isUserAdmin) return;

    const modalRef = this.modalService.open(CreateGroupModalComponent, {
      size: 'lg',
      backdrop: 'static'
    });

    modalRef.result.then(
      (result) => {
        if (result === 'created') {
          this.loadTenantGroups();
          this.toastService.show({
            type: 'success',
            data: [this.translateService.instant('TenantGroupManagement.createSuccess')]
          });
        }
      },
      () => {} // Modal dismissed
    );
  }

  openEditModal(group: any): void {
    if (!this.isUserAdmin) return;

    const modalRef = this.modalService.open(EditGroupModalComponent, {
      size: 'lg',
      backdrop: 'static'
    });
    modalRef.componentInstance.group = { ...group };

    modalRef.result.then(
      (result) => {
        if (result === 'updated') {
          this.loadTenantGroups();
          this.toastService.show({
            type: 'success',
            data: [this.translateService.instant('TenantGroupManagement.updateSuccess')]
          });
        }
      },
      () => {} // Modal dismissed
    );
  }

  openDeleteModal(group: any): void {
    if (!this.isUserAdmin) return;

    this.groupToDelete = group;
    this.modalRef = this.modalService.open(this.deleteConfirmModal, {
      centered: true,
      backdrop: 'static'
    });
  }

  confirmDelete(): void {
    if (!this.groupToDelete) return;

    this.adminService.deleteGroup(this.groupToDelete.groupID).subscribe({
      next: (response) => {
        if (response?.success) {
          this.loadTenantGroups();
          this.toastService.show({
            type: 'success',
            data: [this.translateService.instant('TenantGroupManagement.deleteSuccess')]
          });
        } else {
          this.toastService.showApiError(response?.errorMessage, this.translateService.instant('TenantGroupManagement.deleteFailed'));
        }
        this.modalRef?.close();
        this.groupToDelete = null;
      },
      error: (error) => {
        console.error('Error deleting group:', error);
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('TenantGroupManagement.deleteFailed')]
        });
        this.modalRef?.close();
        this.groupToDelete = null;
      }
    });
  }

  cancelDelete(): void {
    this.modalRef?.close();
    this.groupToDelete = null;
  }
}
